import React, { useEffect, useRef, useState } from "react";
import {
  Modal,
  Paper,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Grid,
  Divider,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";

interface ModalDeletedRecordDetailProps {
  open: boolean;
  onClose: () => void;
  recordType: "client" | "lend" | null;
  backupId: number | null;
  showAlert: (msg: string, severity?: "error" | "info" | "success" | "warning") => void;
}

const formatCurrency = (value: unknown) =>
  new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", minimumFractionDigits: 0 })
    .format(Number(value || 0));

const formatDateTime = (value: unknown) => {
  if (!value) return "—";
  const d = new Date(String(value).replace(" ", "T"));
  if (isNaN(d.getTime())) return String(value);
  return d.toLocaleString("es-CO");
};

const formatDate = (value: unknown) => {
  if (!value) return "—";
  const d = new Date(String(value).replace(" ", "T"));
  if (isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("es-CO");
};

const Field: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Grid item xs={6} sm={4} md={3}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={500}>
      {value === "" || value === null || value === undefined ? "—" : value}
    </Typography>
  </Grid>
);

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Grid item xs={12}>
    <Typography variant="subtitle2" color="primary" sx={{ mt: 1 }}>
      {children}
    </Typography>
    <Divider sx={{ mb: 1 }} />
  </Grid>
);

const ModalDeletedRecordDetail: React.FC<ModalDeletedRecordDetailProps> = ({
  open, onClose, recordType, backupId, showAlert,
}) => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const reqIdRef = useRef(0);

  useEffect(() => {
    if (!open || !recordType || !backupId) {
      return;
    }

    let active = true;
    const requestId = ++reqIdRef.current;

    (async () => {
      setLoading(true);
      setData(null);
      try {
        const { data: resp } = await axios.get(`/inform/deletedrecords/${recordType}/${backupId}`);
        if (!active || requestId !== reqIdRef.current) return;
        setData(resp?.data || null);
      } catch (error) {
        if (active) {
          console.error(error);
          showAlert("No fue posible cargar el detalle del registro borrado.", "error");
        }
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => { active = false; };
  }, [open, recordType, backupId, showAlert]);

  const title = recordType === "client" ? "Cliente borrado" : "Préstamo borrado";

  return (
    <Modal open={open} onClose={onClose}>
      <Paper
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "95vw",
          maxWidth: 800,
          maxHeight: "85vh",
          display: "flex",
          flexDirection: "column",
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            p: 2, bgcolor: "primary.main", color: "#fff",
          }}
        >
          <Typography variant="h6">{title}</Typography>
          <IconButton onClick={onClose} sx={{ color: "#fff" }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ p: 2, overflow: "auto" }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : !data ? (
            <Typography variant="body2" color="text.secondary">Sin datos.</Typography>
          ) : (
            <Grid container spacing={2}>
              <SectionTitle>Auditoría del borrado</SectionTitle>
              <Field label="Borrado por" value={data.deleted_by_name || data.deleted_by_username} />
              <Field label="Fecha y hora" value={formatDateTime(data.deleted_at)} />
              <Field label="Oficina" value={data.office_name} />
              <Field label="Cobro" value={data.collect_name} />

              {recordType === "client" ? (
                <>
                  <SectionTitle>Datos del cliente</SectionTitle>
                  <Field label="ID original" value={data.original_client_id} />
                  <Field label="Cédula" value={data.idnumber} />
                  <Field label="Nombre" value={data.fullname} />
                  <Field label="Teléfono" value={data.phone} />
                  <Field label="Celular" value={data.cellphone} />
                  <Field label="Dirección principal" value={data.mainaddress} />
                  <Field label="Dirección secundaria" value={data.secondaddress} />
                  <Field label="Ocupación" value={data.job} />
                  <Field label="Cupo" value={formatCurrency(data.quote)} />
                  <Field label="Punto de referencia" value={data.landmark} />

                  <SectionTitle>Codeudor</SectionTitle>
                  <Field label="Nombre" value={data.coclient_fullname} />
                  <Field label="Cédula" value={data.coclient_idnumber} />
                  <Field label="Dirección" value={data.coclient_address} />
                  <Field label="Teléfono" value={data.coclient_phone} />
                </>
              ) : (
                <>
                  <SectionTitle>Datos del préstamo</SectionTitle>
                  <Field label="ID original" value={data.original_lend_id} />
                  <Field
                    label="Cliente"
                    value={<Chip size="small" label={data.client_name} />}
                  />
                  <Field label="Constante" value={data.constant_name} />
                  <Field label="Contrato" value={data.contract} />
                  <Field label="Ubicación" value={data.location} />

                  <SectionTitle>Valores</SectionTitle>
                  <Field label="Valor prestado" value={formatCurrency(data.value)} />
                  <Field label="Valor total" value={formatCurrency(data.total_value)} />
                  <Field label="Saldo" value={formatCurrency(data.balance)} />
                  <Field label="Utilidad" value={formatCurrency(data.utility)} />
                  <Field label="Cargo por seguro" value={formatCurrency(data.insurance_charge)} />
                  <Field label="Cuota diaria" value={formatCurrency(data.daily_pay_value)} />
                  <Field label="Plazo (días)" value={data.days_term} />
                  <Field label="Días de atraso" value={data.days_late} />
                  <Field label="Pagado acumulado" value={formatCurrency(data.current_pay)} />

                  <SectionTitle>Fechas</SectionTitle>
                  <Field label="Fecha préstamo" value={formatDate(data.date)} />
                  <Field label="Fecha creación" value={formatDateTime(data.createdAt)} />
                  <Field label="Fecha inicio" value={formatDate(data.date_start)} />
                  <Field label="Fecha refinanciación" value={formatDateTime(data.date_refinance)} />
                  <Field label="Fecha estado" value={formatDateTime(data.date_state)} />
                </>
              )}
            </Grid>
          )}
        </Box>
      </Paper>
    </Modal>
  );
};

export default ModalDeletedRecordDetail;
